import React, { useLayoutEffect, useRef, useState } from 'react';
import { PaginationListType } from '../../Types/PaginationParams';
import { Button } from './Button';
import { RootState, useAppSelector } from '../../store';
interface PaginationProps {
    listType: PaginationListType;
    currentPage: number;
    itemsPerPage: number;
    onPageChange: (newPage: number, listType: PaginationListType) => void;
    totalItems: number;
}

const Pagination: React.FC<PaginationProps> = ({ listType, currentPage, itemsPerPage, onPageChange, totalItems }) => {
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);

    const maxPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
    const containerRef = useRef<HTMLDivElement>(null);
    const [screenSize, setScreenSize] = useState<'large' | 'small'>('large');
    const [isMounted, setIsMounted] = useState(false);

    useLayoutEffect(() => {
        setIsMounted(true);
    }, []);

    // Minimum width for Large version:
    // - Prev Button: ~110px
    // - Next Button: ~110px
    // - Button "1": 50px
    // - Ellipse "...": 50px
    // - Button "104": 60px
    // - Button "105": 60px
    // - Button "106": 60px
    // - Ellipse "...": 50px
    // - Button "131": 60px
    // - Gaps (8x ~12px): 96px
    // - Padding + buffer: 64px
    // = Total: ~770px
    const LARGE_VERSION_MIN_WIDTH = 770;

    useLayoutEffect(() => {
        if (!isMounted) return;

        function checkWidth() {
            const containerWidth = containerRef.current?.offsetWidth || 500;
            setScreenSize(containerWidth >= LARGE_VERSION_MIN_WIDTH + 20 ? 'large' : 'small');
        }

        checkWidth();
        window.addEventListener('resize', checkWidth);

        return () => {
            window.removeEventListener('resize', checkWidth);
        };
    }, [isMounted]);

    useLayoutEffect(() => {
        if (!isMounted) return;

        const timeoutId = setTimeout(() => {
            const containerWidth = containerRef.current?.offsetWidth || 500;
            setScreenSize(containerWidth >= LARGE_VERSION_MIN_WIDTH + 20 ? 'large' : 'small');
        }, 0);

        return () => clearTimeout(timeoutId);
    }, [isMounted, maxPages]);

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= maxPages) {
            onPageChange(newPage, listType);
        }
    };

    // Small: only current page + buttons for prev/next
    // Large: prevButton 1 ... 104 105 106 ... 131 nextButton
    const getPageNumbers = () => {
        if (screenSize === 'small') {
            return [currentPage];
        }

        // Large version with ellipsis pattern
        const pages: (number | string)[] = [];
        const maxPagesNum = Number(maxPages);

        if (maxPagesNum <= 7) {
            // If less than 7 pages, show all
            for (let i = 1; i <= maxPagesNum; i++) pages.push(i);
            return pages;
        }

        // Always first page
        pages.push(1);

        // range around current page (3 pages: current-1, current, current+1)
        let start = Math.max(2, currentPage - 1);
        let end = Math.min(maxPagesNum - 1, currentPage + 1);

        // we always have 3 pages in the middle (if possible)
        if (end - start < 2) {
            if (start === 2) {
                end = Math.min(maxPagesNum - 1, start + 2);
            } else if (end === maxPagesNum - 1) {
                start = Math.max(2, end - 2);
            }
        }

        // Ellipse after 1 if needed
        if (start > 2) {
            pages.push('...');
        }

        // Middle pages
        for (let i = start; i <= end; i++) {
            pages.push(i);
        }

        // Ellipse before last page if needed
        if (end < maxPagesNum - 1) {
            pages.push('...');
        }

        // Always last page if not included
        if (maxPagesNum > 1) {
            pages.push(maxPagesNum);
        }

        return pages;
    };

    if (totalItems === 0 || maxPages <= 1) return null;
    const pageNumbers = getPageNumbers();

    return (
        <div ref={containerRef} className={`w-full flex flex-col items-center space-y-4 py-4`}>
            <div className="flex items-center justify-between space-x-2 w-full px-4">
                <Button
                    variant={currentPage !== 1 ? 'secondary' : 'disabled'}
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                >
                    {i18n.translate({ id: 'pagination.previous', sourceName: 'Main' })}
                </Button>

                <div className="flex items-center gap-3">
                    {screenSize === 'small' ? (
                        <Button variant="secondary" onClick={undefined}>
                            {currentPage}
                        </Button>
                    ) : (
                        pageNumbers.map((pageNumber, index) => (
                            <React.Fragment key={index}>
                                {pageNumber === '...' ? (
                                    <span className="text-layout-textcolor h-[50px] w-[50px] flex items-center justify-center">
                                        …
                                    </span>
                                ) : (
                                    <Button
                                        variant={currentPage === pageNumber ? 'secondary' : 'transparentRound'}
                                        className={`flex items-center justify-center ${
                                            pageNumber === 1 || pageNumber.toString().length === 1
                                                ? '!h-[50px] !w-[50px] !p-0'
                                                : '!h-[50px] !min-w-[60px] !px-3'
                                        } ${currentPage === pageNumber ? '!cursor-default' : ''}`}
                                        disabled={currentPage === pageNumber}
                                        onClick={
                                            currentPage === pageNumber
                                                ? undefined
                                                : () => {
                                                      if (typeof pageNumber === 'number') {
                                                          handlePageChange(pageNumber);
                                                      }
                                                  }
                                        }
                                    >
                                        {pageNumber}
                                    </Button>
                                )}
                            </React.Fragment>
                        ))
                    )}
                </div>

                <Button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === maxPages}
                    variant={currentPage !== maxPages ? 'secondary' : 'disabled'}
                >
                    {i18n.translate({ id: 'pagination.next', sourceName: 'Main' })}
                </Button>
            </div>
        </div>
    );
};

export default Pagination;
